import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack, Typography, FormControlLabel, Checkbox, ToggleButton, ToggleButtonGroup, TextField, MenuItem } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker, MobileTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ACCENT, TEXT_SECONDARY, TEXT_TERTIARY, noMotion } from '../../designsystem/tokens';

/**
 * MetadataSection — renders entity-specific behavioral UI.
 *
 * - Call: Direction selector (INBOUND/OUTBOUND)
 * - Meeting: Calendar checkboxes (createMeeting, createGoogleMeet)
 * - Other types (deal included): renders nothing — deal pipeline/stage live inside DealMetadata.
 */
const DURATION_OPTIONS = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1h' },
    { value: 90, label: '90 min' },
    { value: 120, label: '2h' },
];

const MetadataSection = ({ objectType, metadata, onChange, showGoogleMeet, properties }) => {
    const { t } = useTranslation();

    const updateMetadata = (key, value) => {
        onChange({ ...metadata, [key]: value });
    };

    switch (objectType) {
        case 'call':
            return (
                <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontWeight: 600, mb: 1, display: 'block' }}>
                        {t('direction')}
                    </Typography>
                    <ToggleButtonGroup
                        value={metadata.direction || 'OUTBOUND'}
                        exclusive
                        onChange={(_, val) => { if (val) updateMetadata('direction', val); }}
                        aria-label={t('direction')}
                        sx={{ width: '100%' }}
                    >
                        <ToggleButton
                            value="INBOUND"
                            data-testid="direction-inbound"
                            aria-label="Inbound"
                            sx={{
                                flex: 1,
                                minHeight: 44,
                                borderRadius: '12px !important',
                                color: TEXT_SECONDARY,
                                '&.Mui-selected': { backgroundColor: `${ACCENT}20`, color: ACCENT },
                                ...noMotion,
                            }}
                        >
                            {t('inbound')}
                        </ToggleButton>
                        <ToggleButton
                            value="OUTBOUND"
                            data-testid="direction-outbound"
                            aria-label="Outbound"
                            sx={{
                                flex: 1,
                                minHeight: 44,
                                borderRadius: '12px !important',
                                color: TEXT_SECONDARY,
                                '&.Mui-selected': { backgroundColor: `${ACCENT}20`, color: ACCENT },
                                ...noMotion,
                            }}
                        >
                            {t('outbound')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            );

        case 'meeting':
            return <MeetingMetadata
                metadata={metadata}
                onChange={onChange}
                showGoogleMeet={showGoogleMeet}
                properties={properties}
                t={t}
            />;

        default:
            return null;
    }
};

/**
 * MeetingMetadata — date picker + time picker + duration selector.
 * Computes startDate/endTime ISO strings and pushes them via metadata.
 */
const MeetingMetadata = ({ metadata, onChange, showGoogleMeet, properties, t }) => {
    // Initialize from property values
    const startProp = (properties || []).find(p => p.name === 'startDate');
    const endProp = (properties || []).find(p => p.name === 'endTime');

    const initialStart = useMemo(() => {
        const val = startProp?.value || metadata.computedStartDate;
        return val ? dayjs(val) : dayjs();
    }, []);

    const initialDuration = useMemo(() => {
        const val = endProp?.value || metadata.computedEndTime;
        if (val && startProp?.value) {
            const diff = dayjs(val).diff(dayjs(startProp.value), 'minute');
            const match = DURATION_OPTIONS.find(o => o.value === diff);
            if (match) return diff;
        }
        return 60;
    }, []);

    const [meetingDate, setMeetingDate] = useState(initialStart);
    const [meetingTime, setMeetingTime] = useState(initialStart);
    const [duration, setDuration] = useState(initialDuration);

    // Compute and push ISO strings when date/time/duration change
    useEffect(() => {
        const combined = meetingDate
            .hour(meetingTime.hour())
            .minute(meetingTime.minute())
            .second(0)
            .millisecond(0);
        const endTime = combined.add(duration, 'minute');
        const startISO = combined.toISOString();
        const endISO = endTime.toISOString();

        if (startISO !== metadata.computedStartDate || endISO !== metadata.computedEndTime) {
            onChange({
                ...metadata,
                computedStartDate: startISO,
                computedEndTime: endISO,
            });
        }
    }, [meetingDate, meetingTime, duration]);

    const endTime = meetingDate
        .hour(meetingTime.hour())
        .minute(meetingTime.minute())
        .add(duration, 'minute');

    return (
        <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack spacing={2}>
                    <MobileDatePicker
                        label={t('meetingDate')}
                        value={meetingDate}
                        onChange={(val) => { if (val) setMeetingDate(val); }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                'data-testid': 'meeting-date-picker',
                                slotProps: { inputLabel: { shrink: true } },
                                sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 44 } },
                            },
                        }}
                    />
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <MobileTimePicker
                            label={t('startTime')}
                            value={meetingTime}
                            onChange={(val) => { if (val) setMeetingTime(val); }}
                            slotProps={{
                                textField: {
                                    'data-testid': 'meeting-time-picker',
                                    slotProps: { inputLabel: { shrink: true } },
                                    sx: { flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 44 } },
                                },
                            }}
                        />
                        <TextField
                            select
                            label={t('meetingDuration')}
                            value={duration}
                            onChange={(e) => setDuration(Number(e.target.value))}
                            data-testid="meeting-duration-select"
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 44 } }}
                        >
                            {DURATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, fontWeight: 500 }}>
                        {t('endsAt')}: {endTime.format('HH:mm')}
                    </Typography>
                </Stack>
            </LocalizationProvider>

            {showGoogleMeet && (
                <Stack spacing={0.5} sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={metadata.createMeeting || false}
                                onChange={(e) => onChange({ ...metadata, createMeeting: e.target.checked })}
                                data-testid="create-meeting-checkbox"
                                sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={<Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>{t('createCalendarEvent')}</Typography>}
                        sx={{ minHeight: 44, ml: 0 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={metadata.createGoogleMeet || false}
                                onChange={(e) => onChange({ ...metadata, createGoogleMeet: e.target.checked })}
                                data-testid="create-google-meet-checkbox"
                                sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={<Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>{t('createGoogleMeet')}</Typography>}
                        sx={{ minHeight: 44, ml: 0 }}
                    />
                </Stack>
            )}
        </Box>
    );
};

export default MetadataSection;
